import React from "react";
import { useAuth } from "hooks";
import { useGetVendorDataDetailQuery } from "store/services/CanUsers/vendorApi";
import { useAddVendorBankDetailsMutation } from "store/services/vendorProfileApi";
import { toast } from "react-toastify";
import {
    AccessTime,
    AccountCircle,
    AccountBalance,
    CreditCard,
    Add,
    ModeEditOutlineTwoTone,
    AccountBalanceWalletOutlined,
    StoreOutlined,
    LocationCity,
    Phone,
    Home,
    Person,
    Web,
    ExpandMore,
    CheckCircleOutline,
    ErrorOutline,
} from "@mui/icons-material";
import {
    Box,
    Grid,
    Divider,
    Typography,
    Button,
    IconButton,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    Stack,
    Skeleton,
    Tooltip,
    alpha,
    useTheme,
} from "@mui/material";
import InformationTile from "components/Shared/InformationTile";
import moment from "moment";
import FormDrawer from "components/Shared/FormDrawer";
import VendorProfileBankDetailsForm from "./Components/VendorProfileBankDetailsForm";
import VendorProfileDocumentsSection from "./VendorProfileDocumentsSection";

/* ─── helpers ───────────────────────────────────────────────────────────── */

const formatDate = (d) => moment(d).format("MMM D, YYYY");

const initials = (name = "") =>
    name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

/* ─── sub-components ────────────────────────────────────────────────────── */

/** Top vendor identity card */
function VendorHeader({ vendor }) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 3,
                p: 3,
                mb: 3,
                borderRadius: 3,
                border: `0.5px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
            }}
        >
            {/* avatar + identity */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}>
                <Avatar
                    sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        fontSize: 22,
                        fontWeight: 600,
                        flexShrink: 0,
                    }}
                >
                    {initials(vendor.name)}
                </Avatar>

                <Box>
                    <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
                        {vendor.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {vendor.type}
                    </Typography>
                    <Chip
                        label={vendor.email}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.75, fontSize: 11, height: 22 }}
                    />
                </Box>
            </Box>

            {/* quick stats */}
            <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", sm: "block" } }}
            />

            <Grid
                container
                spacing={2}
                sx={{ flex: 2, alignContent: "flex-start" }}
            >
                {[
                    { icon: <Phone fontSize="small" />, label: "Phone", value: vendor.phone },
                    { icon: <Home fontSize="small" />, label: "Address", value: vendor.address },
                    { icon: <Person fontSize="small" />, label: "Controlling user", value: vendor.controllingUser?.name || "N/A" },
                    { icon: <AccessTime fontSize="small" />, label: "Registered", value: formatDate(vendor.createdAt) },
                ].map(({ icon, label, value }) => (
                    <Grid item xs={12} sm={6} key={label}>
                        <InformationTile
                            icon={() => React.cloneElement(icon, { color: "primary" })}
                            title={label}
                            subtitle={value}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

/** Section heading used inside AccordionDetails */
function SectionLabel({ children }) {
    return (
        <Typography
            variant="overline"
            fontWeight={600}
            color="text.secondary"
            sx={{ letterSpacing: 1, display: "block", mb: 1.5 }}
        >
            {children}
        </Typography>
    );
}

/** Bank info display or empty CTA */
function BankInfoPanel({ bankDetail, onEdit }) {
    const theme = useTheme();

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                }}
            >
                <SectionLabel>Bank information</SectionLabel>
                <Tooltip title="Edit bank details">
                    <IconButton size="small" onClick={onEdit} aria-label="Edit bank details">
                        <ModeEditOutlineTwoTone fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {bankDetail ? (
                <Grid container spacing={2}>
                    {[
                        { icon: <AccountBalance />, label: "Bank", value: bankDetail.bankName },
                        { icon: <AccountCircle />, label: "Account name", value: bankDetail.bankAccountName },
                        { icon: <CreditCard />, label: "Account number", value: bankDetail.bankAccountNumber },
                    ].map(({ icon, label, value }) => (
                        <Grid item xs={12} sm={6} md={4} key={label}>
                            <InformationTile
                                icon={() => React.cloneElement(icon, { color: "primary", fontSize: "small" })}
                                title={label}
                                subtitle={value}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        py: 3,
                        border: `0.5px dashed ${theme.palette.divider}`,
                        borderRadius: 2,
                        color: "text.secondary",
                    }}
                >
                    <AccountBalance sx={{ fontSize: 28, opacity: 0.35 }} />
                    <Typography variant="body2" color="text.secondary">
                        No bank details added yet
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={onEdit}
                        sx={{ mt: 0.5, textTransform: "none" }}
                    >
                        Add bank details
                    </Button>
                </Box>
            )}
        </Box>
    );
}

/** Single vendor profile accordion */
function ProfileAccordion({ profile, index, onEditBank }) {
    const theme = useTheme();
    const activeBankDetail =
        profile?.bankAccount?.find((b) => b.status === "Active") ||
        profile?.bankAccount?.[0] ||
        profile?.vendorProfileBankDetail;
    const hasBankDetails = Boolean(activeBankDetail?.bankName);
    const branches = profile.serviceBranches
        ?.filter((sb) => sb.branch?.length > 0)
        .flatMap((sb) => sb.branch) ?? [];

    return (
        <Accordion
            disableGutters
            elevation={0}
            sx={{
                border: `0.5px solid ${theme.palette.divider}`,
                borderRadius: "12px !important",
                overflow: "hidden",
                "&:before": { display: "none" },
                "&.Mui-expanded": {
                    boxShadow: `0 0 0 1.5px ${theme.palette.primary.main}22`,
                },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                    px: 2.5,
                    minHeight: 60,
                    bgcolor: "background.paper",
                    "&.Mui-expanded": {
                        borderBottom: `0.5px solid ${theme.palette.divider}`,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                        flex: 1,
                        mr: 1,
                    }}
                >
                    {/* shop avatar */}
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <StoreOutlined
                            sx={{ fontSize: 18, color: "primary.main" }}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {profile.shopName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Profile {index + 1}
                            {profile.ownerName ? ` · ${profile.ownerName}` : ""}
                        </Typography>
                    </Box>

                    {/* status badges */}
                    <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                        {hasBankDetails ? (
                            <Chip
                                icon={<CheckCircleOutline sx={{ fontSize: "14px !important" }} />}
                                label="Bank added"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 22 }}
                            />
                        ) : (
                            <Chip
                                icon={<ErrorOutline sx={{ fontSize: "14px !important" }} />}
                                label="No bank"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 22 }}
                            />
                        )}
                    </Stack>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, bgcolor: "background.default" }}>
                {/* ── Profile details ── */}
                <SectionLabel>Profile details</SectionLabel>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                        { icon: <Person />, label: "Owner", value: profile.ownerName },
                        { icon: <Phone />, label: "Phone", value: profile.phone },
                        { icon: <Home />, label: "Address", value: profile.address },
                        { icon: <Web />, label: "Website", value: profile.website || "N/A" },
                        profile?.vendorLimits?.creditLimit && {
                            icon: <AccountBalanceWalletOutlined />,
                            label: "Credit limit",
                            value: profile.vendorLimits.creditLimit,
                        },
                        {
                            icon: <Person />,
                            label: "Relationship manager",
                            value: `${profile.relationshipManager.name} (${profile.relationshipManager.email})`,
                        },
                        {
                            icon: <Person />,
                            label: "Referred by",
                            value: `${profile.referredBy.name} (${profile.referredBy.email})`,
                        },
                        {
                            icon: <Person />,
                            label: "Onboarded by",
                            value: `${profile.onboardedBy.name} (${profile.onboardedBy.email})`,
                        },
                    ]
                        .filter(Boolean)
                        .map(({ icon, label, value }) => (
                            <Grid item xs={12} sm={6} md={4} key={label}>
                                <InformationTile
                                    icon={() =>
                                        React.cloneElement(icon, {
                                            color: "primary",
                                            fontSize: "small",
                                        })
                                    }
                                    title={label}
                                    subtitle={value}
                                />
                            </Grid>
                        ))}
                </Grid>

                {/* ── Service branches ── */}
                {branches.length > 0 && (
                    <>
                        <Divider sx={{ mb: 2.5 }} />
                        <SectionLabel>Service branches</SectionLabel>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 3 }}>
                            {branches.map((branch, i) => (
                                <Chip
                                    key={i}
                                    icon={<LocationCity sx={{ fontSize: "14px !important" }} />}
                                    label={`${branch.name} (${branch.code})`}
                                    variant="outlined"
                                    size="small"
                                    sx={{ fontSize: 12 }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {/* ── Bank info ── */}
                <Divider sx={{ mb: 2.5 }} />
                <BankInfoPanel
                    bankDetail={activeBankDetail}
                    onEdit={() => onEditBank(profile._id)}
                />

                {/* ── Documents ── */}
                <VendorProfileDocumentsSection profile={profile} />
            </AccordionDetails>
        </Accordion>
    );
}

/** Full-page skeleton */
function PageSkeleton() {
    return (
        <Box sx={{ p: 3 }}>
            <Skeleton variant="rounded" height={140} sx={{ mb: 3, borderRadius: 3 }} />
            <Skeleton width={160} height={28} sx={{ mb: 2 }} />
            {[1, 2].map((n) => (
                <Skeleton
                    key={n}
                    variant="rounded"
                    height={64}
                    sx={{ mb: 1.5, borderRadius: 2 }}
                />
            ))}
        </Box>
    );
}

/* ─── main component ────────────────────────────────────────────────────── */

export const VendorSideProfilesDetail = () => {
    const { loggedInUser } = useAuth();
    const vendorId = loggedInUser?.typeRef;

    const {
        data: vendorData,
        isLoading,
        error,
        refetch,
    } = useGetVendorDataDetailQuery(vendorId);

    const [showBankDetailsForm, setShowBankDetailsForm] = React.useState(false);
    const [currentProfileId, setCurrentProfileId] = React.useState(null);
    const [currentBankDetails, setCurrentBankDetails] = React.useState(null);

    const [addBankDetails, { isLoading: addingBankDetails }] =
        useAddVendorBankDetailsMutation();

    const handleSubmit = async (data) => {
        try {
            await addBankDetails({ vendorId: currentProfileId, ...data }).unwrap();
            toast.success("Bank details saved.");
            setShowBankDetailsForm(false);
            refetch();
        } catch (err) {
            toast.error(err?.data?.message ?? "Couldn't save bank details.");
        }
    };

    const handleEditBank = (profileId) => {
        const profile = vendorData.vendorProfiles.find((p) => p._id === profileId);
        setCurrentProfileId(profileId);
        setCurrentBankDetails(profile?.vendorProfileBankDetail ?? null);
        setShowBankDetailsForm(true);
    };

    if (isLoading) return <PageSkeleton />;

    if (error)
        return (
            <Box sx={{ textAlign: "center", color: "error.main", mt: 8 }}>
                <ErrorOutline sx={{ fontSize: 40, mb: 1 }} />
                <Typography>Failed to load vendor data</Typography>
            </Box>
        );

    if (!vendorData)
        return (
            <Box sx={{ textAlign: "center", color: "text.secondary", mt: 8 }}>
                <Typography>No vendor data found.</Typography>
            </Box>
        );

    return (
        <Box sx={{ bgcolor: "background.default", p: { xs: 2, sm: 3 }, minHeight: "100vh" }}>
            {/* vendor header */}
            <VendorHeader vendor={vendorData} />

            {/* profiles list */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography variant="subtitle1" fontWeight={600}>
                    Vendor profiles
                </Typography>
                <Chip
                    label={vendorData.vendorProfiles.length}
                    size="small"
                    sx={{ height: 20, fontSize: 11 }}
                />
            </Box>

            <Stack spacing={1.5}>
                {vendorData.vendorProfiles.map((profile, index) => (
                    <ProfileAccordion
                        key={profile._id}
                        profile={profile}
                        index={index}
                        onEditBank={handleEditBank}
                    />
                ))}
            </Stack>

            {/* bank details drawer */}
            <FormDrawer
                open={showBankDetailsForm}
                onClose={() => setShowBankDetailsForm(false)}
            >
                <VendorProfileBankDetailsForm
                    isBusy={addingBankDetails}
                    onSubmit={handleSubmit}
                    bankDetails={currentBankDetails}
                    vendorId={currentProfileId}
                    onCancel={() => setShowBankDetailsForm(false)}
                />
            </FormDrawer>
        </Box>
    );
};